import * as React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

type InfoCardVariant = 'info' | 'warning';

interface InfoCardProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
  className?: string;
  variant?: InfoCardVariant;
}

const variantStyles: Record<
  InfoCardVariant,
  { container: string; icon: string; text: string; Icon: typeof Info }
> = {
  info: {
    container: 'border-blue-200 bg-blue-50',
    icon: 'text-blue-600',
    text: 'text-blue-800',
    Icon: Info,
  },
  warning: {
    container: 'border-yellow-200 bg-yellow-50',
    icon: 'text-yellow-600',
    text: 'text-yellow-800',
    Icon: AlertTriangle,
  },
};

const InfoCard = React.forwardRef<HTMLDivElement, InfoCardProps>(
  ({ children, className, variant = 'info', ...props }, ref) => {
    const styles = variantStyles[variant];
    const Icon = styles.Icon;

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-2 rounded-lg border p-2.5',
          styles.container,
          className,
        )}
        {...props}
      >
        <Icon
          aria-hidden='true'
          className={cn('h-4 w-4 flex-shrink-0', styles.icon)}
        />
        <p className={cn('text-sm', styles.text)}>{children}</p>
      </div>
    );
  },
);
InfoCard.displayName = 'InfoCard';

export { InfoCard };
